import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { ContentLoader, SwitchChipGroup, Text } from '@frontend/design-system';
import { useCallIntelStatsComparisonData } from '../../hooks/useCallIntelStatsComparisonData';
import { PercentChangeIndicator } from '../percent-change-indicator';

dayjs.extend(utc);

export const SummaryPanel: FC<{
  locationIds: string[];
  contactTypeFilter: CallIntelTypes.ContactTypeEnum[];
  setContactTypeFilter: Dispatch<SetStateAction<CallIntelTypes.ContactTypeEnum[]>>;
}> = ({ locationIds = [], contactTypeFilter, setContactTypeFilter }) => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  const { currentStats, previousStats, isLoading } = useCallIntelStatsComparisonData({
    locationIds,
    includesAnalysis: [],
    // Added opportunities true to match with the other call intel widget and prevent extra API calls
    statsIncludes: { opportunities: true, serviceQuality: false },
    comparisonPeriod: 'week',
    fetchOverviewDetails: false,
    filters: {
      contactTypes: contactTypeFilter,
    },
  });

  const totalCallsAnalyzed = currentStats?.totalCallsAnalyzed ?? 0;

  const { scheduledRate, unscheduledCount, rateChange } = useMemo(() => {
    const scheduledCount = currentStats?.summary?.scheduledOpportunities ?? 0;
    const totalCount = currentStats?.summary?.totalOpportunities ?? 0;
    const unscheduledCount = totalCount - scheduledCount;

    const scheduledRate = Math.round(totalCount > 0 ? (scheduledCount / totalCount) * 100 : 0);

    const previousScheduledCount = previousStats?.summary?.scheduledOpportunities ?? 0;
    const previousTotalCount = previousStats?.summary?.totalOpportunities ?? 0;
    const previousScheduledRate = Math.round(
      previousTotalCount > 0 ? (previousScheduledCount / previousTotalCount) * 100 : 0
    );

    return {
      unscheduledCount,
      scheduledRate,
      rateChange: scheduledRate - previousScheduledRate,
    };
  }, [
    currentStats?.summary?.scheduledOpportunities,
    currentStats?.summary?.totalOpportunities,
    previousStats?.summary?.scheduledOpportunities,
    previousStats?.summary?.totalOpportunities,
  ]);

  return (
    <div css={styles.summaryPanel}>
      <ContentLoader show={isLoading} />
      <SwitchChipGroup
        css={{ margin: theme.spacing(0.5) }}
        value={contactTypeFilter as string[]}
        setValue={setContactTypeFilter as Dispatch<SetStateAction<string[]>>}
        singleSelect={true}
      >
        <SwitchChipGroup.Option
          trackingId='call-intel-unscheduled-widget-new-patient-filter-button'
          value={CallIntelTypes.ContactTypeEnum.CONTACT_NEW_PATIENT}
        >
          {t('New Patient')}
        </SwitchChipGroup.Option>
        <SwitchChipGroup.Option
          trackingId='call-intel-unscheduled-widget-existing-patient-filter-button'
          value={CallIntelTypes.ContactTypeEnum.CONTACT_EXISTING_PATIENT}
        >
          {t('Existing Patient')}
        </SwitchChipGroup.Option>
      </SwitchChipGroup>

      <div css={styles.content}>
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <Text as='span' weight='bold' size='large' css={styles.countHeader}>
            {unscheduledCount}
          </Text>
          <Text size='medium' color='subdued'>
            {t('Unscheduled')}
          </Text>
        </div>
        <div css={styles.rateChange}>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Text color='subdued' as='span' weight='bold' css={{ fontSize: theme.font.size.h2 }}>
              {scheduledRate}%
            </Text>
            {!!totalCallsAnalyzed && <PercentChangeIndicator percentChange={rateChange} comparisonPeriod='week' />}
          </div>
          <Text as='span' color='subdued' size='medium'>
            {t('Scheduled Rate')}
          </Text>
        </div>
      </div>
    </div>
  );
};

const styles = {
  summaryPanel: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
  `,
  countHeader: css`
    font-size: ${theme.fontSize(138)};
    line-height: 1;
    overflow: hidden;
    @container (max-width: ${breakpoints.small.min}px) {
      font-size: ${theme.fontSize(96)};
    }
  `,

  content: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: row;
      flex-wrap: wrap;
      row-gap: ${theme.spacing(0.5)};
      justify-content: flex-start;
      align-items: flex-end;
    }
  `,
  rateChange: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-end;
  `,
};
